import React, { useMemo } from "react";
import { Box } from "@mui/material";
import dayjs from "dayjs";
import { ProjectLeadDto, ProjectLeadStatus } from "../../../types/projectLeadTypes";
import { ProjectOverviewDto } from "../../../types/projectTypes";
import KanbanColumn from "./KanbanColumn";
import { PROJECT_KANBAN_COLUMNS, ProjectKanbanStatus } from "../../../utils/projectStatusUtils";
import { KanbanBoardItem } from "./kanbanTypes";

interface ProjectKanbanBoardProps {
  projects: ProjectOverviewDto[];
  projectLeads: ProjectLeadDto[];
}

const mapProjectToKanbanItem = (project: ProjectOverviewDto): KanbanBoardItem => {
  const displayDate = project.kickoff
    ? dayjs(project.kickoff).format("DD.MM.YYYY")
    : project.tenderDate
    ? dayjs(project.tenderDate).format("DD.MM.YYYY")
    : null;

  const estimatedBT =
    project.estimatedProjectBTmin && project.estimatedProjectBTmax
      ? `${project.estimatedProjectBTmin}-${project.estimatedProjectBTmax}`
      : null;

  const soldBT = project.soldBT ?? null;
  const companyName = project.client?.companyName ?? null;
  const plName = project.pl ? `${project.pl.firstname} ${project.pl.lastname}` : null;

  return {
    id: `project-${project.projectId}`,
    kind: "project",
    status: project.status as ProjectKanbanStatus,
    title: project.projectName,
    href: `/projekte/${project.projectId}`,
    metricValue: soldBT,
    details: [
      soldBT ? `${soldBT} BT` : estimatedBT ? `~${estimatedBT} BT` : null,
      displayDate,
      plName,
      companyName,
    ].filter((detail): detail is string => Boolean(detail)),
  };
};

const mapProjectLeadToKanbanItem = (projectLead: ProjectLeadDto): KanbanBoardItem | null => {
  if (projectLead.status === ProjectLeadStatus.Converted) {
    return null;
  }

  return {
    id: `projectLead-${projectLead.leadId}`,
    kind: "projectLead",
    status: projectLead.status === ProjectLeadStatus.Discarded ? ProjectLeadStatus.Discarded : "Lead",
    title: projectLead.projectName,
    href: `/leads/${projectLead.leadId}`,
    details: [
      `Angelegt am ${dayjs(projectLead.createdAt).format("DD.MM.YYYY")}`,
      projectLead.creatorName ?? `Member #${projectLead.creatorId}`,
      projectLead.customer ? `Kunde: ${projectLead.customer}` : null,
      projectLead.revenue ? `Umsatz: ${projectLead.revenue}` : null,
      projectLead.status === ProjectLeadStatus.Discarded && projectLead.discardReason
        ? `Grund: ${projectLead.discardReason}`
        : projectLead.timeframe
        ? `Zeitraum: ${projectLead.timeframe}`
        : null,
    ].filter((detail): detail is string => Boolean(detail)),
  };
};

/**
 * Kanban board showing projects and leads grouped by status.
 */
const ProjectKanbanBoard = ({ projects, projectLeads }: ProjectKanbanBoardProps) => {
  const items = useMemo(() => {
    const projectLeadItems = projectLeads
      .map(mapProjectLeadToKanbanItem)
      .filter((item): item is KanbanBoardItem => item !== null);
    return [...projectLeadItems, ...projects.map(mapProjectToKanbanItem)];
  }, [projectLeads, projects]);

  const itemsByStatus = useMemo(() => {
    const grouped: Record<string, KanbanBoardItem[]> = {};

    for (const column of PROJECT_KANBAN_COLUMNS) {
      grouped[column.status] = [];
    }

    for (const item of items) {
      if (grouped[item.status]) {
        grouped[item.status].push(item);
      }
    }

    return grouped;
  }, [items]);

  return (
    <Box
      sx={{
        display: "flex",
        gap: 1.5,
        overflowX: "auto",
        pb: 2,
        pt: 1,
        width: "100%",
        alignItems: "flex-start",
        scrollbarWidth: "thin",
        "&::-webkit-scrollbar": { height: 6 },
        "&::-webkit-scrollbar-thumb": { bgcolor: "divider", borderRadius: 3 },
      }}
    >
      {PROJECT_KANBAN_COLUMNS.map((column) => (
        <KanbanColumn
          key={column.status}
          label={column.label}
          color={column.color}
          items={itemsByStatus[column.status] ?? []}
        />
      ))}
    </Box>
  );
};

export default ProjectKanbanBoard;
